import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import { BLOCKED_QUERY_SEGMENTS } from '#/lib/blockedQuerySegments';
import { fetchJobs } from '#/lib/jobs';
import {
  doesPathExist,
  fetchPagePaths,
} from '#/lib/sanity/fetchers/fetchPagePaths';
import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import {
  draftPageProps,
  pageProps,
  PageProps as PagePropsSchema,
} from '#/lib/sanity/queries/page';
import { site } from '#/lib/sanity/queries/site';
import type { PageInitialData } from '#/lib/types';
import { extractSlugFromRouter } from '#/lib/utils';
import { Page } from '#/components/Page';
import { aboutSchema } from '#/components/SeoSchema/aboutSchema';
import { RenderSEOSchema } from '#/components/SeoSchema/Render';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchPagePaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

const publishedPageDataQuery = groq`{
  'page': ${pageProps},
  'site': ${site},
}`;

const draftPageDataQuery = groq`{
  'page': ${draftPageProps},
  'site': ${site},
}`;

type SlugPageQueryResult = Omit<
  NextPageData<typeof PagePropsSchema.shape.page>,
  'draftMode'
>;

type SlugPageProps = NextPageData<typeof PagePropsSchema.shape.page> & {
  initial: QueryResponseInitial<SlugPageQueryResult>;
  initialData?: PageInitialData;
  isPreview?: boolean;
};

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string[] }>) {
  const slug = params?.slug.join('/');

  if (
    slug &&
    BLOCKED_QUERY_SEGMENTS.some((segment) => slug.includes(segment))
  ) {
    return {
      notFound: true,
    };
  }

  const exists = await doesPathExist({ slug, draftMode });
  if (!exists) {
    return {
      notFound: true,
    };
  }

  const query = draftMode ? draftPageDataQuery : publishedPageDataQuery;
  const initial = await loadQuery<SlugPageQueryResult>(
    query,
    { slug },
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof PagePropsSchema.shape.page>({
    schema: PagePropsSchema,
    result: initial.data,
    draftMode,
    context: { callerName: '[...slug] getStaticProps', slug },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  let jobs = null;
  // Only fetch jobs server-side if this is the careers page
  if (slug === 'careers') {
    jobs = await fetchJobs();
  }

  return {
    props: {
      ...props,
      initial,
      initialData: { jobs },
      isPreview: draftMode ?? false,
    },
  };
}

export default function PageRoute(props: SlugPageProps) {
  const { query } = useRouter();
  const slug = extractSlugFromRouter(query);
  const pageQuery = props.draftMode
    ? draftPageDataQuery
    : publishedPageDataQuery;
  const { data } = useQuery<SlugPageQueryResult>(
    pageQuery,
    { slug },
    {
      initial: props.initial,
    },
  );
  const page = data.page ?? props.page;

  return (
    <>
      {slug === 'about' && <RenderSEOSchema schemaData={aboutSchema} />}
      <Page
        sections={page?.sections}
        initialData={props.initialData}
        isPreview={props.isPreview}
        sanitySource={
          page
            ? {
                documentId: page._id,
                documentType: 'page',
                path: 'sections',
              }
            : undefined
        }
      />
    </>
  );
}
